import { Button, ButtonStyle } from '@atlas/components';
import classNames from 'classnames';
import DomPurify from 'dompurify';
import { useMemo } from 'react';
import { useLocation } from 'wouter';

import { staticPageRequest } from 'api/cms';
import Icon from 'components/Icon/Icon.tsx';
import Loader from 'components/Loader';
import Meta from 'components/Meta';
import NotFound from 'components/NotFound';
import SubHeader from 'components/SubHeader';
import { useLanguage } from 'store';
import useLayout, { LayoutType } from 'utils/layout';
import { LoadingState } from 'utils/types';
import useRequest from 'utils/useRequest';

import styles from './StaticPage.module.scss';

const StaticPage = () => {
  const [slug] = useLocation();
  const layout = useLayout();
  const [
    {
      current: { code: lang },
    },
  ] = useLanguage();

  const pageName = useMemo(() => {
    const parts = slug.split('/');
    return parts.length > 2 ? `/${parts.slice(2).join('/')}` : slug;
  }, [slug]);

  const { data, loading } = useRequest(
    () =>
      staticPageRequest({
        query: {
          lang,
          name: pageName,
        },
      }),
    [pageName, lang],
  );

  const content: string | undefined = useMemo(
    () => (data?.data ? DomPurify.sanitize(data.data) : undefined),
    [data],
  );

  if ([LoadingState.initial, LoadingState.loading].includes(loading)) {
    return <Loader />;
  }

  if (
    loading === LoadingState.failed ||
    !data ||
    !content ||
    data.slug !== slug
  ) {
    return <NotFound />;
  }

  const {
    name,
    meta_title,
    meta_description,
    meta_keywords,
    og_title,
    og_description,
  } = data;

  return (
    <div className={classNames(styles.wrapper, styles[layout])}>
      <Meta
        title={meta_title}
        metaDescription={meta_description}
        metaKeywords={meta_keywords}
        ogTitle={og_title}
        ogDescription={og_description}
      />
      {layout === LayoutType.mobile && (
        <SubHeader>
          <Icon name="book.bold" width={24} height={24} />
          {name}
        </SubHeader>
      )}
      <aside className={styles['print-button']}>
        <Button style={ButtonStyle.outline} onClick={() => window.print()}>
          Print
        </Button>
      </aside>
      <article
        className={styles.content}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized HTML from CMS
        dangerouslySetInnerHTML={{ __html: content }}
      />
    </div>
  );
};

export default StaticPage;
